import { makeAutoObservable } from 'mobx';
import { ProductLineId } from '@insurancemenu/imenu-components-lib/dist/types/ProductLineId';
import {
  getProductLinesFromArray,
  PRODUCT_LINES_CONTENT_MAP,
  sortByProductLineId,
} from '@insurancemenu/imenu-components-lib/dist/utils/ProductLinesHelper';
import { EnrollmentStage, EnrollmentType, InstalledEnrollmentType } from 'types/enrollmentService';
import { UserType } from 'types/general';
import { dateFormatters } from '@insurancemenu/imenu-components-lib/dist/helpers';
import { EmailDelegate } from './emailDelegate';
import { TriggerRenewalsPeriod } from './types';

const availableForEmailsStages: EnrollmentStage[] = [EnrollmentStage.RENEWAL, EnrollmentStage.INSTALLED];

interface EnrollmentProduct {
  carrierProductName: string;
  rates: Array<{ renewalRate: string }>;
}

interface EnrollmentProductLineData {
  id: ProductLineId;
  type: EnrollmentType;
  exhibitUrls: string[];
  rateGuaranteeEndDate: string | null;
  terminationEffectiveDate?: string;
  products: EnrollmentProduct[];
}

interface EnrollmentData {
  id: string;
  groupId: string;
  stage: EnrollmentStage;
  effectiveDate: string;
  productLineIds: ProductLineId[];
  productLines: EnrollmentProductLineData[];
}

export interface TriggerRenewalsEnrollmentActions {
  moveToRenewal: TriggerRenewalsActionState;
  manageEmails: TriggerRenewalsActionState;
  manageExhibits: TriggerRenewalsActionState;
  changeEffectiveDate: TriggerRenewalsActionState;
}

export interface TriggerRenewalsActionState {
  isVisible: boolean;
  isDisabled: boolean;
}

function isTerminated(line: EnrollmentProductLineData): boolean {
  return line.type === EnrollmentType.TERMINATED;
}

function isRateMissing(rate: { renewalRate: string }): boolean {
  return rate.renewalRate.length === 0;
}

function areAllProductRatesMissing(product: EnrollmentProduct): boolean {
  return product.rates.length > 0 && product.rates.every(isRateMissing);
}

function areSomeProductRatesMissing(product: EnrollmentProduct): boolean {
  return product.rates.some(isRateMissing) && product.rates.some((rate) => !isRateMissing(rate));
}

function areRatesMissing(line: EnrollmentProductLineData): boolean {
  return !isTerminated(line) && !line.products.some((product) => product.rates.some((rate) => !isRateMissing(rate)));
}

function toEnrollmentData(period: InstalledEnrollmentType): EnrollmentData {
  return {
    id: period.id,
    groupId: period.groupId,
    stage: period.stage,
    effectiveDate: period.effectiveDate,
    productLineIds: period.productLineIds,
    productLines:
      period.installedProposal?.productLines.map((line) => ({
        id: line.id,
        type: line.type,
        exhibitUrls: line.exhibitUrls ?? [],
        rateGuaranteeEndDate: line.additionalSyncData?.rateGuaranteeEndDate || null,
        terminationEffectiveDate: line.terminationEffectiveDate ?? undefined,
        products: line.products.map((product) => ({
          carrierProductName: product.carrierProductName,
          rates: product.rates.map((rate) => ({ renewalRate: rate.renewalRate })),
        })),
      })) ?? [],
  };
}

function getMissingExhibitLineIds(productLines: EnrollmentProductLineData[]): ProductLineId[] {
  return productLines.filter((line) => line.exhibitUrls.length === 0 && !isTerminated(line)).map((line) => line.id);
}

function getMissingProductsOrRates(
  productLines: EnrollmentProductLineData[],
): { message: string; type: 'ERROR' | 'WARNING' } | null {
  const missingProducts = productLines.filter((line) => !isTerminated(line) && line.products.length === 0);
  if (missingProducts.length > 0) {
    const missingLinesNames = missingProducts.map((line) => PRODUCT_LINES_CONTENT_MAP[line.id].label);
    return {
      message: `Missing products for: ${missingLinesNames.join(', ')}`,
      type: 'ERROR',
    };
  }

  const products = productLines.filter((line) => !isTerminated(line)).flatMap((line) => line.products);

  const productsWithAllRatesMissing = products.filter(areAllProductRatesMissing);
  if (productsWithAllRatesMissing.length > 0) {
    const productNames = productsWithAllRatesMissing.map((product) => product.carrierProductName);

    return {
      message: `Missing rates for: ${productNames.join(', ')}`,
      type: 'ERROR',
    };
  }

  const productsWithSomeRatesMissing = products.filter(areSomeProductRatesMissing);
  if (productsWithSomeRatesMissing.length > 0) {
    const productNames = productsWithSomeRatesMissing.map((product) => product.carrierProductName);

    return {
      message: `Mismatched rates for: ${productNames.join(', ')}`,
      type: 'WARNING',
    };
  }

  return null;
}

export class EnrollmentDelegate {
  private _data: EnrollmentData;
  private _emailDelegate: EmailDelegate;

  public get period(): TriggerRenewalsPeriod {
    return {
      id: this._data.id,
      groupId: this._data.groupId,
      effectiveDate: this._data.effectiveDate,
      productLineIds: this._data.productLineIds,
      hasAllExhibits: !this.areExhibitsMissing,
      hasAllRates: !this.areRatesMissing,
    };
  }

  public get stage(): EnrollmentStage {
    return this._data.stage;
  }

  public get isRenewalOrInstalledStage(): boolean {
    return this._data.stage === EnrollmentStage.INSTALLED || this._data.stage === EnrollmentStage.RENEWAL;
  }

  public get emailDelegate(): EmailDelegate {
    return this._emailDelegate;
  }

  public get areEmailsAvailable(): boolean {
    return availableForEmailsStages.includes(this._data.stage);
  }

  public get isAvailableForBatchActions(): boolean {
    return this.isRenewalOrInstalledStage && !this._areAllProductLinesTerminated;
  }

  public get batchActionsUnavailableReason(): string {
    if (!this.isRenewalOrInstalledStage) {
      return 'Renewal and Installed periods only';
    }

    if (this._areAllProductLinesTerminated) {
      return 'All product lines are terminated';
    }

    return '';
  }

  public get emailUserTypesCount(): number {
    if (!this.areEmailsAvailable) {
      return 0;
    }

    return [UserType.AOR, UserType.ADMIN].filter((userType) => this._emailDelegate.getEmailsByUserType(userType).length)
      .length;
  }

  public get areExhibitsMissing(): boolean {
    return getMissingExhibitLineIds(this._data.productLines).length > 0;
  }

  public get areRatesMissing(): boolean {
    return this._data.productLines.some(areRatesMissing);
  }

  private get _areAllProductLinesTerminated(): boolean {
    return (
      this.allEnrollmentProductLines.length > 0 && this.allEnrollmentProductLines.every((line) => line.isTerminated)
    );
  }

  public get enrollmentActions(): TriggerRenewalsEnrollmentActions {
    return {
      moveToRenewal: {
        isVisible: this._data.stage === EnrollmentStage.INSTALLED && !this._areAllProductLinesTerminated,
        isDisabled: this.areExhibitsMissing || this.areRatesMissing,
      },
      manageEmails: {
        isVisible: this.isRenewalOrInstalledStage && !this._areAllProductLinesTerminated,
        isDisabled: false,
      },
      manageExhibits: {
        isVisible: this.isRenewalOrInstalledStage && !this._areAllProductLinesTerminated,
        isDisabled: this.exhibitProductLines.length < 1,
      },
      changeEffectiveDate: {
        isVisible: !this._areAllProductLinesTerminated,
        isDisabled: false,
      },
    };
  }

  public get pLinesWithRateGuaranteeEarlierThanEffectiveDate(): { name: string; rateGuaranteeEndDate: string }[] {
    return this._data.productLines
      .filter(
        (line) =>
          !isTerminated(line) && !!line.rateGuaranteeEndDate && line.rateGuaranteeEndDate < this._data.effectiveDate,
      )
      .map((line) => ({
        name: PRODUCT_LINES_CONTENT_MAP[line.id].label,
        rateGuaranteeEndDate: dateFormatters.normDateToShortAmericanDate(line.rateGuaranteeEndDate ?? ''),
      }));
  }

  public get hasMismatchedProductsAndExhibits(): boolean {
    return this._data.productLines.some((line) => line.products.length !== line.exhibitUrls.length);
  }

  public get allEnrollmentProductLines(): Array<{ id: ProductLineId; isTerminated: boolean }> {
    const terminatedIds = new Set(this._data.productLines.filter(isTerminated).map(({ id }) => id));
    const productLineIds = new Set([...this._data.productLineIds, ...terminatedIds]);

    return [...productLineIds]
      .map((id) => ({
        id,
        isTerminated: terminatedIds.has(id),
      }))
      .sort((a, b) => sortByProductLineId(a.id, b.id));
  }

  public get missingExhibitText(): string {
    const missingExhibitLineIds = getMissingExhibitLineIds(this._data.productLines);

    return missingExhibitLineIds.length > 0
      ? `Missing exhibits for: ${getProductLinesFromArray(missingExhibitLineIds)}`
      : '';
  }

  public get terminatedProductLinesText(): string {
    const terminatedLines = this._data.productLines.filter(isTerminated);
    if (terminatedLines.length === 0) {
      return '';
    }

    const linesText = terminatedLines
      .map((line) => {
        const name = PRODUCT_LINES_CONTENT_MAP[line.id].label;
        const terminationDate = line.terminationEffectiveDate
          ? dateFormatters.normDateToShortAmericanDate(line.terminationEffectiveDate)
          : '';

        return terminationDate ? `${name} – ${terminationDate}` : name;
      })
      .join(', ');

    return `Terminated product lines: ${linesText}`;
  }

  public get missingProductsOrRates(): { message: string; type: 'ERROR' | 'WARNING' } | null {
    return getMissingProductsOrRates(this._data.productLines);
  }

  public get exhibitProductLines(): Array<{ id: ProductLineId; exhibitUrls: string[] }> {
    return this._data.productLines.map((line) => ({ id: line.id, exhibitUrls: line.exhibitUrls }));
  }

  public update(period: InstalledEnrollmentType): void {
    if (period.id !== this._data.id) {
      throw new Error(`Tried to update enrollment delegate ${this._data.id} with period ${period.id}`);
    }

    this._data = toEnrollmentData(period);
  }

  public replaceEmailDelegate(emailDelegate: EmailDelegate): void {
    this._emailDelegate = emailDelegate;
  }

  constructor(period: InstalledEnrollmentType, emailDelegate: EmailDelegate) {
    this._data = toEnrollmentData(period);
    this._emailDelegate = emailDelegate;
    makeAutoObservable(this, {}, { autoBind: true });
  }
}
